import { AvailabilityException, AvailabilityRule } from '../types';
import { timeToMinutes, weekdayOf } from './calendarTime';

export type Interval = [number, number];

export interface AvailabilityData {
  profileId: string;
  rules: AvailabilityRule[];
  exceptions: AvailabilityException[];
}

const merge = (intervals: Interval[]): Interval[] => {
  const merged: Interval[] = [];
  [...intervals]
    .sort((a, b) => a[0] - b[0])
    .forEach(([start, end]) => {
      const last = merged[merged.length - 1];
      if (last && start <= last[1]) last[1] = Math.max(last[1], end);
      else merged.push([start, end]);
    });
  return merged;
};

const ruleIntervals = (rules: AvailabilityRule[]): Interval[] => {
  if (rules.length === 0 || rules.some(rule => rule.is_closed)) return [];
  return merge(
    rules.map(rule => [timeToMinutes(rule.start_time), timeToMinutes(rule.end_time)] as Interval)
  );
};

const covers = (exception: AvailabilityException, date: string): boolean =>
  exception.date <= date && date <= (exception.date_end ?? exception.date);

const customHours = (exceptions: AvailabilityException[]): Interval[] =>
  exceptions
    .filter(
      exception => exception.type === 'custom_hours' && exception.start_time && exception.end_time
    )
    .map(
      exception =>
        [timeToMinutes(exception.start_time), timeToMinutes(exception.end_time)] as Interval
    );

const subtract = (intervals: Interval[], [from, to]: Interval): Interval[] => {
  const result: Interval[] = [];
  intervals.forEach(([start, end]) => {
    if (end <= from || start >= to) {
      result.push([start, end]);
      return;
    }
    if (start < from) result.push([start, from]);
    if (end > to) result.push([to, end]);
  });
  return result;
};

const applyClosures = (intervals: Interval[], exceptions: AvailabilityException[]): Interval[] => {
  let result = intervals;
  for (const exception of exceptions) {
    if (exception.type !== 'closed') continue;
    if (!exception.start_time || !exception.end_time) return [];
    result = subtract(result, [
      timeToMinutes(exception.start_time),
      timeToMinutes(exception.end_time),
    ]);
  }
  return result;
};

// Mirrors the engine: profile closure > resource exception > resource rule > profile rule.
export const workingIntervals = (
  data: AvailabilityData,
  resourceId: string,
  date: string
): Interval[] => {
  const weekday = weekdayOf(date);
  const rulesFor = (owner: (rule: AvailabilityRule) => boolean) =>
    data.rules.filter(rule => rule.weekday === weekday && owner(rule));
  const exceptionsFor = (owner: (exception: AvailabilityException) => boolean) =>
    data.exceptions.filter(exception => owner(exception) && covers(exception, date));

  const profileExceptions = exceptionsFor(exception => exception.profile === data.profileId);
  const resourceExceptions = exceptionsFor(exception => exception.resource === resourceId);
  const ownRules = rulesFor(rule => rule.resource === resourceId);

  let intervals: Interval[];
  if (ownRules.length > 0) {
    intervals = ruleIntervals(ownRules);
  } else {
    const profileCustom = customHours(profileExceptions);
    intervals =
      profileCustom.length > 0
        ? merge(profileCustom)
        : ruleIntervals(rulesFor(rule => rule.profile === data.profileId));
  }
  const resourceCustom = customHours(resourceExceptions);
  if (resourceCustom.length > 0) intervals = merge(resourceCustom);
  return applyClosures(applyClosures(intervals, resourceExceptions), profileExceptions);
};

export const unionIntervals = (lists: Interval[][]): Interval[] => merge(lists.flat());
